import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Button, Modal, Form, ProgressBar, Badge, Alert, Accordion } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { FiCheck, FiX, FiMessageSquare, FiClock, FiCamera, FiAlertCircle, FiLock, FiMail, FiBell, FiChevronDown } from 'react-icons/fi';
import { Header, BottomNav, SkeletonList, ImageViewerModal } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { useDate } from '../context/DateContext';
import { cleaningApi, memoApi, messageApi, noticeApi, CleaningItem, MemoComment, DirectMessage, Memo } from '../api/client';
import { formatRoomAddress, formatRoomType, formatDbTime } from '../utils';

export const MyCleaningPage = () => {
  const { user } = useAuth();
  const { selectedDate } = useDate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageAccordionRef = useRef<HTMLDivElement>(null);
  const [selectedRoom, setSelectedRoom] = useState<CleaningItem | null>(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [memoText, setMemoText] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [viewerImage, setViewerImage] = useState<string | null>(null);
  const [hasVibrated, setHasVibrated] = useState(false);

  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  // 내 객실만 조회
  const { data, isLoading } = useQuery({
    queryKey: ['cleaning-list', dateStr, user?.id],
    queryFn: () => cleaningApi.getList(dateStr, user?.id),
  });

  // 선택된 객실의 메모 히스토리
  const { data: memoHistoryData, isLoading: memoHistoryLoading } = useQuery({
    queryKey: ['memo-history', dateStr, selectedRoom?.roomId],
    queryFn: () => memoApi.getComments(dateStr, selectedRoom!.roomId),
    enabled: !!selectedRoom && showHistoryModal,
  });

  // 받은 메시지 조회
  const { data: messagesData } = useQuery({
    queryKey: ['received-messages', dateStr],
    queryFn: () => messageApi.getReceivedMessages(dateStr),
  });

  // 공지사항 조회
  const { data: noticeData } = useQuery({
    queryKey: ['notice', dateStr],
    queryFn: () => noticeApi.getNotice(dateStr),
  });

  // 객실별 전달사항 조회
  const { data: memosData } = useQuery({
    queryKey: ['memos', dateStr],
    queryFn: () => memoApi.getMemos(dateStr),
  });

  // 메시지 읽음 처리
  const markAsReadMutation = useMutation({
    mutationFn: (messageId: number) => messageApi.markAsRead(messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['received-messages'] });
    },
  });

  const toggleCompleteMutation = useMutation({
    mutationFn: (listId: number) => cleaningApi.toggleComplete(listId),
    onSuccess: (response) => {
      if (response.data.success) {
        queryClient.invalidateQueries({ queryKey: ['cleaning-list'] });
        toast.success(response.data.message || '상태가 변경되었습니다.');
        setShowActionModal(false);
      }
    },
    onError: () => {
      toast.error('작업에 실패했습니다.');
    },
  });

  const toggleCancelMutation = useMutation({
    mutationFn: (listId: number) => cleaningApi.toggleCancel(listId),
    onSuccess: (response) => {
      if (response.data.success) {
        queryClient.invalidateQueries({ queryKey: ['cleaning-list'] });
        toast.success(response.data.message || '취소 처리되었습니다.');
        setShowActionModal(false);
      }
    },
    onError: () => {
      toast.error('작업에 실패했습니다.');
    },
  });

  const toggleNoshowMutation = useMutation({
    mutationFn: (listId: number) => cleaningApi.toggleNoshow(listId),
    onSuccess: (response) => {
      if (response.data.success) {
        queryClient.invalidateQueries({ queryKey: ['cleaning-list'] });
        toast.success(response.data.message || '노쇼 처리되었습니다.');
        setShowActionModal(false);
      }
    },
    onError: () => {
      toast.error('작업에 실패했습니다.');
    },
  });

  const addMemoMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRoom) return Promise.reject('No room selected');
      return memoApi.addCommentWithImage(dateStr, selectedRoom.roomId, memoText, selectedImage || undefined);
    },
    onSuccess: () => {
      toast.success('메모가 추가되었습니다.');
      setMemoText('');
      setSelectedImage(null);
      setImagePreview(null);
      setShowMemoModal(false);
      queryClient.invalidateQueries({ queryKey: ['cleaning-list'] });
      queryClient.invalidateQueries({ queryKey: ['memo-history'] });
    },
    onError: () => {
      toast.error('메모 추가에 실패했습니다.');
    },
  });

  const handleRoomClick = (item: CleaningItem) => {
    setSelectedRoom(item);
    setShowActionModal(true);
  };

  const handleAddMemo = () => {
    setShowActionModal(false);
    setShowMemoModal(true);
  };

  const handleViewMemoHistory = () => {
    setShowActionModal(false);
    setShowHistoryModal(true);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const list = data?.data.data?.list || [];

  // 내 객실만 필터링
  const myRooms = list.filter((item: CleaningItem) => item.userId === user?.id);
  const completedCount = myRooms.filter((item: CleaningItem) => item.completed).length;
  const canceledCount = myRooms.filter((item: CleaningItem) => item.canceled).length;
  const activeCount = myRooms.length - canceledCount;
  const progressPercent = activeCount > 0 ? Math.round((completedCount / activeCount) * 100) : 0;

  const memoHistory = memoHistoryData?.data.data?.comments || [];
  const receivedMessages = messagesData?.data.data?.messages || [];
  const unreadMessages = receivedMessages.filter((msg: DirectMessage) => !msg.isRead);
  const dailyNotice = noticeData?.data.data?.notice;
  const roomMemos = memosData?.data.data?.memos || [];

  // 새 메시지가 있으면 진동 + 사운드
  useEffect(() => {
    if (unreadMessages.length > 0 && !hasVibrated) {
      if ('vibrate' in navigator) {
        navigator.vibrate([200, 100, 200]); // 진동 패턴
      }
      // 사운드 재생
      const audio = new Audio('/msg_sound.mp3');
      audio.play().catch(() => {}); // 자동 재생 실패 무시
      setHasVibrated(true);
    }
  }, [unreadMessages.length, hasVibrated]);

  // 날짜 변경 시 진동 상태 초기화
  useEffect(() => {
    setHasVibrated(false);
  }, [dateStr]);

  // 메시지 섹션으로 스크롤
  const scrollToMessages = () => {
    messageAccordionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // 객실별 전달사항 찾기 헬퍼
  const getRoomMemo = (roomId: string): string | undefined => {
    const memo = roomMemos.find((m: Memo) => m.roomId === roomId);
    return memo?.memo;
  };

  return (
    <div className="app-container">
      <Header
        showDatePicker
        showProfile
        userName={user?.name}
        onProfileClick={() => navigate('/settings')}
      />

      <main className="main-content">
        {/* 새 메시지 상단 배너 */}
        {unreadMessages.length > 0 && (
          <div className="new-message-banner" onClick={scrollToMessages}>
            <div className="banner-content">
              <FiMail className="banner-icon" size={20} />
              <span>{unreadMessages.length}개의 새 메시지가 있습니다</span>
            </div>
            <FiChevronDown />
          </div>
        )}

        {/* 공지사항 */}
        {dailyNotice?.notice && (
          <Alert variant="info" className="mb-3">
            <div className="d-flex align-items-start">
              <FiBell className="me-2 mt-1 flex-shrink-0" />
              <div>
                <strong>오늘의 공지사항</strong>
                <div className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{dailyNotice.notice}</div>
              </div>
            </div>
          </Alert>
        )}

        {/* 받은 메시지 */}
        {receivedMessages.length > 0 && (
          <Accordion
            ref={messageAccordionRef}
            className={`mb-3 ${unreadMessages.length > 0 ? 'message-accordion-highlight' : ''}`}
            defaultActiveKey={unreadMessages.length > 0 ? '0' : undefined}
          >
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <FiMail className="me-2" />
                받은 메시지 ({receivedMessages.length})
                {unreadMessages.length > 0 && (
                  <Badge bg="danger" className="ms-2 pulse-animation">{unreadMessages.length} 새 메시지</Badge>
                )}
              </Accordion.Header>
              <Accordion.Body className="p-0">
                <div className="list-group list-group-flush">
                  {receivedMessages.map((msg: DirectMessage) => (
                    <div
                      key={msg.id}
                      className={`list-group-item ${!msg.isRead ? 'bg-light' : ''}`}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-1">
                            <strong className="me-2">{msg.sender.name}</strong>
                            <small className="text-muted">
                              {formatDbTime(msg.createdAt, 'HH:mm')}
                            </small>
                            {!msg.isRead && (
                              <Badge bg="primary" className="ms-2" style={{ fontSize: '10px' }}>NEW</Badge>
                            )}
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</div>
                        </div>
                        {!msg.isRead && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => markAsReadMutation.mutate(msg.id)}
                            disabled={markAsReadMutation.isPending}
                          >
                            <FiCheck />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        )}

        {/* 내 진행률 */}
        <div className="bg-white rounded-3 p-3 mb-3 shadow-sm">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-bold">내 청소 진행률</span>
            <span className="text-primary fw-bold">{progressPercent}%</span>
          </div>
          <ProgressBar
            now={progressPercent}
            variant={progressPercent === 100 ? 'success' : 'primary'}
            style={{ height: '10px' }}
          />
          <div className="d-flex justify-content-between mt-2 small text-muted">
            <span>완료 {completedCount} / {activeCount}</span>
            {canceledCount > 0 && <span className="text-danger">취소 {canceledCount}</span>}
          </div>
        </div>

        {/* 내 객실 목록 */}
        <h6 className="mb-3">내가 선택한 객실 ({myRooms.length})</h6>

        {isLoading ? (
          <SkeletonList count={4} />
        ) : myRooms.length === 0 ? (
          <div className="text-center text-muted py-5">
            <FiCheck size={48} className="mb-3 opacity-50" />
            <p>선택한 객실이 없습니다.</p>
            <Button variant="primary" onClick={() => navigate('/')}>
              객실 선택하러 가기
            </Button>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {myRooms.map((item: CleaningItem) => (
              <div
                key={item.id}
                className={`bg-white rounded-3 p-3 shadow-sm ${item.completed ? 'border-start border-success border-4' : ''} ${item.canceled ? 'opacity-50' : ''}`}
                onClick={() => handleRoomClick(item)}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="fw-bold">
                      {formatRoomAddress(item.roomInfo?.room_address) || item.roomId}
                      {item.isJaesil && (
                        <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '10px' }}>
                          재실
                        </Badge>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {item.roomInfo?.room_type && (
                        <small className="text-muted">{formatRoomType(item.roomInfo.room_type)}</small>
                      )}
                      {item.password && (
                        <small className="text-success">
                          <FiLock className="me-1" style={{ fontSize: '10px' }} />
                          {item.password}
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {(item.memoCount ?? 0) > 0 && (
                      <Badge bg="info" pill>
                        <FiMessageSquare className="me-1" />
                        {item.memoCount}
                      </Badge>
                    )}
                    {item.canceled ? (
                      <Badge bg="danger">
                        {item.noshow ? '노쇼' : '취소'}
                      </Badge>
                    ) : item.completed ? (
                      <Badge bg="success">
                        <FiCheck className="me-1" />
                        완료
                      </Badge>
                    ) : (
                      <Badge bg="primary">진행중</Badge>
                    )}
                  </div>
                </div>

                {/* 전달사항 표시 */}
                {getRoomMemo(item.roomId) && (
                  <div className="mt-2 small">
                    <div className="text-info">
                      <FiMessageSquare className="me-1" />
                      <strong>전달:</strong> {getRoomMemo(item.roomId)}
                    </div>
                  </div>
                )}

                {/* 체크인/체크아웃 메모 표시 */}
                {(item.checkinMemo || item.checkoutMemo) && (
                  <div className="mt-2 small">
                    {item.checkoutMemo && (
                      <div className="text-danger mb-1">
                        <FiAlertCircle className="me-1" />
                        <strong>퇴실:</strong> {item.checkoutMemo}
                      </div>
                    )}
                    {item.checkinMemo && (
                      <div className="text-primary">
                        <FiAlertCircle className="me-1" />
                        <strong>입실:</strong> {item.checkinMemo}
                      </div>
                    )}
                  </div>
                )}

                {item.completedTime && (
                  <div className="mt-2 small text-muted">
                    <FiClock className="me-1" />
                    {formatDbTime(item.completedTime, 'HH:mm')} 완료
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      <BottomNav />

      {/* 객실 액션 모달 */}
      <Modal show={showActionModal} onHide={() => setShowActionModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {formatRoomAddress(selectedRoom?.roomInfo?.room_address) || selectedRoom?.roomId}
            {selectedRoom?.isJaesil && (
              <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '12px' }}>
                재실
              </Badge>
            )}
            {selectedRoom?.canceled ? (
              <Badge bg="danger" className="ms-2" style={{ fontSize: '12px' }}>
                {selectedRoom.noshow ? '노쇼' : '취소'}
              </Badge>
            ) : selectedRoom?.completed ? (
              <Badge bg="success" className="ms-2" style={{ fontSize: '12px' }}>
                <FiCheck className="me-1" />완료
              </Badge>
            ) : (
              <Badge bg="primary" className="ms-2" style={{ fontSize: '12px' }}>
                진행중
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* 객실 정보 */}
          <div className="mb-3 p-2 bg-light rounded">
            <div className="d-flex justify-content-between">
              <span className="text-muted">타입</span>
              <span>{formatRoomType(selectedRoom?.roomInfo?.room_type)}</span>
            </div>
            {selectedRoom?.password && (
              <div className="d-flex justify-content-between mt-1">
                <span className="text-muted">비밀번호</span>
                <span className="fw-bold text-success">
                  <FiLock className="me-1" />
                  {selectedRoom.password}
                </span>
              </div>
            )}
          </div>

          {/* 전달사항 표시 */}
          {selectedRoom && getRoomMemo(selectedRoom.roomId) && (
            <div className="alert alert-info mb-3 py-2">
              <div className="d-flex align-items-start">
                <FiMessageSquare className="me-2 mt-1 flex-shrink-0" />
                <div>
                  <strong>전달사항</strong>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{getRoomMemo(selectedRoom.roomId)}</div>
                </div>
              </div>
            </div>
          )}

          {/* 체크인/체크아웃 메모 표시 */}
          {(selectedRoom?.checkinMemo || selectedRoom?.checkoutMemo) && (
            <div className="alert alert-warning mb-3 py-2">
              {selectedRoom.checkoutMemo && (
                <div className="mb-1">
                  <strong>퇴실 메모:</strong> {selectedRoom.checkoutMemo}
                </div>
              )}
              {selectedRoom.checkinMemo && (
                <div>
                  <strong>입실 메모:</strong> {selectedRoom.checkinMemo}
                </div>
              )}
            </div>
          )}

          <div className="d-grid gap-2">
            {!selectedRoom?.canceled && (
              <Button
                variant={selectedRoom?.completed ? 'warning' : 'success'}
                size="lg"
                onClick={() => selectedRoom && toggleCompleteMutation.mutate(selectedRoom.id)}
                disabled={toggleCompleteMutation.isPending}
              >
                {selectedRoom?.completed ? (
                  <><FiX className="me-2" />완료 취소</>
                ) : (
                  <><FiCheck className="me-2" />청소 완료</>
                )}
              </Button>
            )}

            <Button
              variant="outline-primary"
              onClick={handleAddMemo}
            >
              <FiMessageSquare className="me-2" />
              메모 추가
            </Button>

            {(selectedRoom?.memoCount ?? 0) > 0 && (
              <Button
                variant="outline-info"
                onClick={handleViewMemoHistory}
              >
                <FiMessageSquare className="me-2" />
                메모 보기 ({selectedRoom?.memoCount})
              </Button>
            )}

            <hr />

            {!selectedRoom?.completed && !selectedRoom?.canceled && (
              <>
                <Button
                  variant="outline-danger"
                  onClick={() => selectedRoom && toggleCancelMutation.mutate(selectedRoom.id)}
                  disabled={toggleCancelMutation.isPending}
                >
                  취소 처리
                </Button>
                <Button
                  variant="outline-danger"
                  onClick={() => selectedRoom && toggleNoshowMutation.mutate(selectedRoom.id)}
                  disabled={toggleNoshowMutation.isPending}
                >
                  노쇼 처리
                </Button>
              </>
            )}

            {selectedRoom?.canceled && (
              <Button
                variant="outline-warning"
                onClick={() => {
                  if (selectedRoom) {
                    // 노쇼 상태면 노쇼 해제, 아니면 취소 해제
                    if (selectedRoom.noshow) {
                      toggleNoshowMutation.mutate(selectedRoom.id);
                    } else {
                      toggleCancelMutation.mutate(selectedRoom.id);
                    }
                  }
                }}
                disabled={toggleCancelMutation.isPending || toggleNoshowMutation.isPending}
              >
                {selectedRoom.noshow ? '노쇼 해제' : '취소 해제'}
              </Button>
            )}

          </div>
        </Modal.Body>
      </Modal>

      {/* 메모 추가 모달 */}
      <Modal show={showMemoModal} onHide={() => { setShowMemoModal(false); handleRemoveImage(); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>메모 추가</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            as="textarea"
            rows={4}
            placeholder="메모 내용을 입력하세요"
            value={memoText}
            onChange={(e) => setMemoText(e.target.value)}
            className="mb-3"
          />

          {/* 이미지 미리보기 */}
          {imagePreview && (
            <div className="position-relative mb-3">
              <img
                src={imagePreview}
                alt="Preview"
                className="img-fluid rounded"
                style={{ maxHeight: '200px' }}
              />
              <Button
                variant="danger"
                size="sm"
                className="position-absolute top-0 end-0 m-1"
                onClick={handleRemoveImage}
              >
                <FiX />
              </Button>
            </div>
          )}

          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            capture="environment"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />

          <Button
            variant="outline-secondary"
            className="w-100"
            onClick={() => fileInputRef.current?.click()}
          >
            <FiCamera className="me-2" />
            사진 촬영
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowMemoModal(false); handleRemoveImage(); }}>
            취소
          </Button>
          <Button
            variant="primary"
            onClick={() => addMemoMutation.mutate()}
            disabled={(!memoText.trim() && !selectedImage) || addMemoMutation.isPending}
          >
            {addMemoMutation.isPending ? '저장 중...' : '저장'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 메모 히스토리 모달 */}
      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {formatRoomAddress(selectedRoom?.roomInfo?.room_address) || selectedRoom?.roomId} 메모
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {memoHistoryLoading ? (
            <div className="text-center py-4">로딩 중...</div>
          ) : memoHistory.length === 0 ? (
            <div className="text-center text-muted py-4">메모가 없습니다.</div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {memoHistory.map((memo: MemoComment) => (
                <div key={memo.id} className="border rounded p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <span className="fw-bold">{memo.user.name}</span>
                    <small className="text-muted">{formatDbTime(memo.createdAt)}</small>
                  </div>
                  <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>{memo.comment}</p>
                  {memo.images && memo.images.length > 0 && (
                    <div className="d-flex gap-2 flex-wrap">
                      {memo.images.map((img, idx) => (
                        <img
                          key={idx}
                          src={img.thumbnailUrl || img.imageUrl}
                          alt="Memo"
                          className="rounded"
                          style={{ maxHeight: '100px', cursor: 'pointer' }}
                          onClick={() => setViewerImage(img.imageUrl)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>
            닫기
          </Button>
          <Button variant="primary" onClick={() => { setShowHistoryModal(false); handleAddMemo(); }}>
            메모 추가
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 이미지 뷰어 모달 */}
      <ImageViewerModal
        show={!!viewerImage}
        imageUrl={viewerImage}
        onHide={() => setViewerImage(null)}
      />
    </div>
  );
};
